/**
 * A capability na URL (F-S6, Bloco 2).
 *
 * O backend cunha um `k` assinado por contexto — o slug aberto, a view pública,
 * o link exportado, o par do compare — e o entrega uma vez, no bootstrap
 * (`url_capability`). Ele carimba o `k` em toda URL que emite; mas o caminho
 * quente **não usa o template do backend**: `materializedPreviewUrl` e
 * `materializedTileUrl` no Viewer, e os quatro construtores do compare, montam
 * `/vc/…` aqui no cliente. Este módulo é o único lugar onde eles anexam o `k`,
 * para não haver sete cópias da mesma linha.
 *
 * Também é por aqui que `apiFetch` anexa o `k` a toda chamada do viewer
 * (`/api/viewer/v1/…` e `/vc/…`): as rotas de bootstrap, documento, busca,
 * alinhamento e regiões passam pelo mesmo funil de posse que as imagens.
 *
 * Não é sessão: quem tem sessão manda o Bearer e dispensa o `k`. É o que
 * abre as imagens para quem **não** pode mandar cabeçalho — `<img src>`,
 * `background-image`, o cache público do NGINX.
 *
 * Em `lib/` e sem DOM de propósito: é conta pura, e conta pura que decide
 * endereço de imagem merece teste próprio.
 */

export interface UrlCapability {
  k: string;
  /** ISO 8601, UTC. O bootstrap é pedido de novo pouco antes disto. */
  expires_at: string;
}

let atual: UrlCapability | null = null;

/** O `k` em vigor, ou `null` quando o bootstrap não trouxe nenhum. */
export function capabilityToken(): string | null {
  return atual?.k ?? null;
}

export function capabilityAtual(): UrlCapability | null {
  return atual;
}

/**
 * Guarda a capability que o bootstrap trouxe. `null`/`undefined` limpa: um
 * payload sem o bloco (backend anterior à F-S6, arquivo sem versão) não pode
 * deixar o `k` de um contexto anterior pendurado nas URLs deste.
 */
export function setCapability(capability: UrlCapability | null | undefined): void {
  atual = capability && typeof capability.k === "string" && capability.k ? capability : null;
}

/** As URLs que recebem o `k`: o viewer pela API e o cache público do NGINX. */
export function precisaDeCapability(url: string): boolean {
  const caminho = caminhoDe(url);
  return caminho.startsWith("/api/viewer/v1/") || caminho.startsWith("/vc/");
}

/**
 * `url` com o `k` em vigor — substituindo um `k` que já esteja lá, porque o
 * carimbado pelo backend pode ser de um bootstrap anterior à renovação.
 * Sem `k` em vigor, ou fora das rotas do viewer, devolve a URL como veio.
 * Aceita caminho relativo (`/vc/…`) e URL absoluta da mesma origem.
 */
export function comCapability(url: string, token: string | null = capabilityToken()): string {
  if (!token || !precisaDeCapability(url)) return url;
  const [semFragmento, fragmento] = separarFragmento(url);
  const indice = semFragmento.indexOf("?");
  const base = indice >= 0 ? semFragmento.slice(0, indice) : semFragmento;
  const query = indice >= 0 ? semFragmento.slice(indice + 1) : "";
  const partes = query
    .split("&")
    .filter((parte) => parte.length > 0 && !parte.startsWith("k="));
  partes.push(`k=${encodeURIComponent(token)}`);
  return `${base}?${partes.join("&")}${fragmento}`;
}

/**
 * Quando pedir o bootstrap de novo: um pouco antes de vencer, nunca depois.
 * `null` quando não há o que renovar. A margem é generosa porque a renovação
 * é barata (um `poll=1`) e um `k` vencido derruba todo tile da tela.
 */
export function momentoDeRenovar(
  capability: UrlCapability | null,
  agora: number = Date.now(),
  margemMs: number = 5 * 60 * 1000,
): number | null {
  if (!capability) return null;
  const vence = Date.parse(capability.expires_at);
  if (!Number.isFinite(vence)) return null;
  // Metade da vida restante, com teto na margem: um prazo curto renova cedo
  // sem virar laço; um prazo de horas renova cinco minutos antes.
  const restante = vence - agora;
  if (restante <= 0) return agora;
  return Math.max(agora, vence - Math.min(margemMs, restante / 2));
}

function separarFragmento(url: string): [string, string] {
  const indice = url.indexOf("#");
  return indice >= 0 ? [url.slice(0, indice), url.slice(indice)] : [url, ""];
}

function caminhoDe(url: string): string {
  if (url.startsWith("/")) return url;
  // URL absoluta: só o caminho interessa, e sem depender de `window`.
  const semEsquema = url.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "");
  const barra = semEsquema.indexOf("/");
  return barra >= 0 ? semEsquema.slice(barra) : "/";
}
